import { useState } from 'react';
import { Calendar, Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useDashboard, type CustomRange } from '@/hooks/useDashboardContext';
import { type PeriodKey } from '@/lib/periods';

const PRESETS: { key: PeriodKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'last_year', label: 'Last year' },
];

function activeLabel(period: PeriodKey, custom: CustomRange | null): string {
  if (period === 'custom' && custom) return `${custom.start} → ${custom.end}`;
  return PRESETS.find((p) => p.key === period)?.label ?? period;
}

export function PeriodPicker({ className }: { className?: string }) {
  const { period, setPeriod, customRange } = useDashboard();
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(customRange?.start ?? '');
  const [draftEnd, setDraftEnd] = useState(customRange?.end ?? '');

  const applyCustom = () => {
    if (!draftStart || !draftEnd) return;
    if (draftStart > draftEnd) return;
    setPeriod('custom', { start: draftStart, end: draftEnd });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-2 rounded-md border border-border/60 bg-card/80 px-2.5 py-1.5 text-xs font-medium shadow-soft',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'hover:border-foreground/30',
            className,
          )}
          aria-haspopup="dialog"
        >
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          <span>{activeLabel(period, customRange)}</span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent>
        <div className="space-y-3">
          <div>
            <div className="px-1 pb-1 text-2xs uppercase tracking-wide text-muted-foreground">
              Presets
            </div>
            <ul className="space-y-0.5">
              {PRESETS.map((opt) => {
                const active = opt.key === period;
                return (
                  <li key={opt.key}>
                    <button
                      type="button"
                      onClick={() => {
                        setPeriod(opt.key);
                        setOpen(false);
                      }}
                      className={cn(
                        'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors',
                        active
                          ? 'bg-accent text-foreground'
                          : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                      )}
                    >
                      <span>{opt.label}</span>
                      {active && <Check className="h-3.5 w-3.5" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="border-t border-border/40 pt-3">
            <div className="px-1 pb-1.5 text-2xs uppercase tracking-wide text-muted-foreground">
              Custom range
            </div>
            <div className="flex items-center gap-2 px-1">
              <input
                type="date"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                className="w-full rounded-md border border-border/60 bg-card px-2 py-1 text-xs"
                aria-label="Start date"
              />
              <span className="text-muted-foreground">→</span>
              <input
                type="date"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value)}
                className="w-full rounded-md border border-border/60 bg-card px-2 py-1 text-xs"
                aria-label="End date"
              />
            </div>
            <button
              type="button"
              disabled={!draftStart || !draftEnd || draftStart > draftEnd}
              onClick={applyCustom}
              className={cn(
                'mt-2 w-full rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                'bg-foreground text-background hover:bg-foreground/90',
                'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-foreground',
              )}
            >
              Apply range
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
